import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  CookieStorage,
} from 'amazon-cognito-identity-js';
import { config } from './config';

/**
 * Cognito wrapper. Hands out a fresh ID token and turns Cognito's callback API
 * into promises with messages a person can act on.
 *
 * Tokens live in a cookie rather than the library's default localStorage,
 * because localStorage is scoped to one origin and this product spans several.
 * A sitter signs in on the platform's own host and their business lives on a
 * subdomain of it; with localStorage they arrived there signed out, landing on
 * the client-facing storefront as though they had never logged in.
 */

export class AuthError extends Error {
  constructor(
    message: string,
    readonly code: string
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * The pool is built lazily. CognitoUserPool throws on a malformed pool id, and
 * building it at module scope would take the whole bundle down before React
 * mounts whenever the Cognito environment variables are missing. Deferring it
 * means an unconfigured build still renders the public storefront and reports
 * a clear error only on the screens that actually need to sign someone in.
 */
/**
 * The narrowest cookie scope that still covers the hop from the platform host
 * to a business's own subdomain.
 *
 * On a white-labelled custom domain there is no shared parent to speak of, so
 * the cookie is scoped to that host alone. Pinning it to the platform domain
 * there would ask the browser to set a cookie for a domain the page is not on,
 * which it silently refuses, and sign-in would break with no error.
 *
 * Locally there are no subdomains and no https, so the library's own
 * localStorage is left alone.
 */
function sessionStore(): CookieStorage | undefined {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return undefined;

  const base = config.appDomain;
  const onPlatform = host === base || host.endsWith(`.${base}`);
  return new CookieStorage({
    domain: onPlatform ? `.${base}` : host,
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
    expires: 30,
  });
}

/** Every CognitoUser needs this passed explicitly; it does not inherit the
 *  pool's storage, and one built with the default would read an empty
 *  localStorage and report the person as signed out. */
function userData(email: string) {
  return { Username: email.toLowerCase(), Pool: getPool(), Storage: sessionStore() };
}

let poolInstance: CognitoUserPool | null = null;

export const isAuthConfigured = (): boolean =>
  Boolean(config.cognito.userPoolId && config.cognito.clientId);

function getPool(): CognitoUserPool {
  if (poolInstance) return poolInstance;
  if (!isAuthConfigured()) {
    throw new AuthError(
      'Sign-in is not configured for this build. Set the Cognito environment variables.',
      'NotConfigured'
    );
  }
  poolInstance = new CognitoUserPool({
    UserPoolId: config.cognito.userPoolId,
    ClientId: config.cognito.clientId,
    Storage: sessionStore(),
  });
  return poolInstance;
}

/** Cognito's codes are not readable; these are. */
function readable(error: { code?: string; message?: string }): AuthError {
  const code = error.code ?? 'UnknownError';
  const messages: Record<string, string> = {
    NotAuthorizedException: 'That email and password do not match.',
    UserNotFoundException: 'That email and password do not match.',
    UserNotConfirmedException: 'Confirm your email address first. Check your inbox for the code.',
    CodeMismatchException: 'That code is not right. Check it and try again.',
    ExpiredCodeException: 'That code has expired. Ask for a new one.',
    UsernameExistsException: 'An account already exists for that email. Sign in instead.',
    InvalidPasswordException:
      'Passwords need at least 12 characters, including an uppercase letter, a lowercase letter and a number.',
    LimitExceededException: 'Too many attempts. Wait a few minutes and try again.',
  };
  return new AuthError(messages[code] ?? error.message ?? 'Something went wrong. Try again.', code);
}

export function currentUser(): CognitoUser | null {
  if (!isAuthConfigured()) return null;
  return getPool().getCurrentUser();
}

/**
 * A valid ID token, refreshing silently if the current one has expired.
 * Returns null when nobody is signed in, which callers treat as anonymous.
 */
export function getIdToken(): Promise<string | null> {
  return new Promise((resolve) => {
    // Anonymous pages call this on every request, so an unconfigured build
    // must resolve to null rather than throw.
    if (!isAuthConfigured()) {
      resolve(null);
      return;
    }
    const user = getPool().getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }
    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  const user = new CognitoUser(userData(email));
  const details = new AuthenticationDetails({ Username: email.toLowerCase(), Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: resolve,
      onFailure: (error) => reject(readable(error)),
      newPasswordRequired: () =>
        reject(new AuthError('This account needs a new password. Contact the sitter.', 'NewPasswordRequired')),
    });
  });
}

export function signUp(email: string, password: string, name: string): Promise<void> {
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: email.toLowerCase() }),
    new CognitoUserAttribute({ Name: 'name', Value: name }),
  ];

  return new Promise((resolve, reject) => {
    getPool().signUp(email.toLowerCase(), password, attributes, [], (error) => {
      if (error) reject(readable(error));
      else resolve();
    });
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  const user = new CognitoUser(userData(email));
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (error) => {
      if (error) reject(readable(error));
      else resolve();
    });
  });
}

export function resendCode(email: string): Promise<void> {
  const user = new CognitoUser(userData(email));
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((error) => {
      if (error) reject(readable(error));
      else resolve();
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser(userData(email));
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (error) => reject(readable(error)),
    });
  });
}

export function resetPassword(email: string, code: string, password: string): Promise<void> {
  const user = new CognitoUser(userData(email));
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, password, {
      onSuccess: () => resolve(),
      onFailure: (error) => reject(readable(error)),
    });
  });
}

export function signOut(): void {
  if (!isAuthConfigured()) return;
  getPool().getCurrentUser()?.signOut();
}
